import React, { useLayoutEffect, useRef } from 'react';
import { UnscheduledEvent } from '../../types';
import { EVENT_STATUS } from '../../constants';
import { CORRECTIVE_ACTION_MIN_LENGTH } from './saveValidation';

interface SummarySectionProps {
  formData: UnscheduledEvent;
  mode: 'view' | 'edit' | 'create';
  isReadOnly: boolean;
  summaryCompleted: boolean;
  manualOverride: boolean;
  setManualOverride: (value: boolean) => void;
  setSummaryCompleted: (value: boolean) => void;
  updateField: (field: keyof UnscheduledEvent, value: string | number | boolean) => void;
  /**
   * Treat the record as if it is being completed even when its live status is
   * not COMPLETED (e.g. during a Move, which closes the source as COMPLETED), so
   * the completion-only fields (Corrective Action, Summary Manual Override)
   * activate and can be filled before the move.
   */
  treatAsCompleted?: boolean;
}

/**
 * Summary (auto-generated, editable via Manual Override) and Corrective Action
 * fields. Corrective Action is shown only when the event is Completed (or being
 * moved) and is highlighted as required while empty.
 */
const SummarySection: React.FC<SummarySectionProps> = ({
  formData,
  mode,
  isReadOnly,
  summaryCompleted,
  manualOverride,
  setManualOverride,
  setSummaryCompleted,
  updateField,
  treatAsCompleted = false,
}) => {
  const isCompleted = formData.status === EVENT_STATUS.COMPLETED || treatAsCompleted;
  const correctiveActionLength = (formData.correctiveAction || '').trim().length;
  const correctiveMissing = !isReadOnly && correctiveActionLength < CORRECTIVE_ACTION_MIN_LENGTH;

  // Grow the Summary textarea to fit its content so the entire summary is
  // visible without scrolling. Runs before paint on any summary change (initial
  // load, generation, or manual-override typing) so there is no flash of a
  // fixed-height box. The CSS min-height keeps a sensible floor when short/empty.
  const summaryRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const element = summaryRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [formData.summary]);

  return (
    <>
      <div className="field-row field-row-tall">
        <label className="field-label field-label-center">
          Summary
          {summaryCompleted && (mode === 'edit' || mode === 'create') && isCompleted && (
            <div className="manual-override-block">
              <span className="manual-override-label">Manual Override</span>
              <input
                type="checkbox"
                checked={manualOverride}
                onChange={(e) => {
                  setManualOverride(e.target.checked);
                  if (!e.target.checked) {
                    setSummaryCompleted(false);
                  }
                }}
              />
            </div>
          )}
        </label>
        <div className="field-group">
          <textarea
            ref={summaryRef}
            className="field-textarea field-textarea-autosize"
            value={formData.summary}
            readOnly={!manualOverride}
            onChange={(e) => updateField('summary', e.target.value)}
            placeholder="Auto-generated summary"
          />
        </div>
      </div>

      {isCompleted && (
        <div className="field-row field-row-tall">
          <label className="field-label">Corrective Action</label>
          <div className="field-group">
            <textarea
              className={`field-textarea${correctiveMissing ? ' field-required-missing' : ''}`}
              value={formData.correctiveAction}
              readOnly={isReadOnly}
              onChange={(e) => updateField('correctiveAction', e.target.value)}
              rows={3}
              placeholder="Enter corrective action"
            />
            {correctiveMissing && (
              <span className="field-inline-error" role="alert">
                Corrective Action must be at least {CORRECTIVE_ACTION_MIN_LENGTH} characters (
                {correctiveActionLength}/{CORRECTIVE_ACTION_MIN_LENGTH}).
              </span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SummarySection;
